import type { FluxogramaShape } from '@/types/fluxograma';

interface ShapeRendererProps {
  shape: FluxogramaShape;
  width: number;
  height: number;
  bgColor?: string;
  borderColor?: string;
  borderWidth?: number;
}

/**
 * Renderiza a silhueta da forma como SVG inline, ocupando 100% do container.
 * Não inclui texto — o label fica como overlay no ShapeNode.
 */
export function ShapeRenderer({
  shape,
  width,
  height,
  bgColor = '#ffffff',
  borderColor = '#0F172A',
  borderWidth = 2,
}: ShapeRendererProps) {
  const w = Math.max(8, width);
  const h = Math.max(8, height);
  const stroke = borderColor;
  const sw = borderWidth;

  const common = {
    fill: bgColor,
    stroke,
    strokeWidth: sw,
    strokeLinejoin: 'round' as const,
    strokeLinecap: 'round' as const,
  };

  if (shape === 'process' || shape === 'bpmn_task' || shape === 'subprocess') {
    const r = shape === 'subprocess' ? 4 : 10;
    return (
      <svg width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <rect x={sw / 2} y={sw / 2} width={w - sw} height={h - sw} rx={r} ry={r} {...common} />
        {shape === 'subprocess' && (
          <>
            <rect x={sw / 2 + 4} y={sw / 2} width={w - sw - 8} height={h - sw} rx={r} ry={r} {...common} fill="none" />
          </>
        )}
      </svg>
    );
  }

  if (shape === 'start_end') {
    const r = h / 2;
    return (
      <svg width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <rect x={sw / 2} y={sw / 2} width={w - sw} height={h - sw} rx={r} ry={r} {...common} />
      </svg>
    );
  }

  if (shape === 'decision') {
    const cx = w / 2;
    const cy = h / 2;
    return (
      <svg width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <polygon points={`${cx},${sw} ${w - sw},${cy} ${cx},${h - sw} ${sw},${cy}`} {...common} />
      </svg>
    );
  }

  if (shape === 'input_output') {
    const skew = w * 0.12;
    return (
      <svg width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <polygon
          points={`${skew},${sw} ${w - sw},${sw} ${w - skew},${h - sw} ${sw},${h - sw}`}
          {...common}
        />
      </svg>
    );
  }

  if (shape === 'document') {
    const wave = h * 0.18;
    const d = `M ${sw},${sw} L ${w - sw},${sw} L ${w - sw},${h - wave} Q ${(3 * w) / 4},${h} ${w / 2},${h - wave / 2} Q ${w / 4},${h - wave} ${sw},${h - wave / 2} Z`;
    return (
      <svg width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <path d={d} {...common} />
      </svg>
    );
  }

  if (shape === 'database') {
    const ry = Math.min(14, h * 0.12);
    return (
      <svg width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <path
          d={`M ${sw},${ry} a ${(w - sw * 2) / 2},${ry} 0 0 1 ${w - sw * 2},0 L ${w - sw},${h - ry} a ${(w - sw * 2) / 2},${ry} 0 0 1 -${w - sw * 2},0 Z`}
          {...common}
        />
        <path
          d={`M ${sw},${ry} a ${(w - sw * 2) / 2},${ry} 0 0 0 ${w - sw * 2},0`}
          fill="none"
          stroke={stroke}
          strokeWidth={sw}
        />
      </svg>
    );
  }

  if (shape === 'connector') {
    const r = Math.min(w, h) / 2 - sw / 2;
    return (
      <svg width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <circle cx={w / 2} cy={h / 2} r={r} {...common} />
      </svg>
    );
  }

  if (shape === 'bpmn_event_start' || shape === 'bpmn_event_end') {
    const r = Math.min(w, h) / 2 - sw / 2;
    return (
      <svg width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <circle
          cx={w / 2}
          cy={h / 2}
          r={r}
          {...common}
          strokeWidth={shape === 'bpmn_event_end' ? sw * 2.2 : sw}
        />
      </svg>
    );
  }

  if (shape === 'bpmn_event_timer' || shape === 'bpmn_event_message') {
    // Evento intermediário BPMN: círculo duplo (borda externa + interna finas),
    // com glifo interno indicando o tipo (relógio ou envelope).
    const rOuter = Math.min(w, h) / 2 - sw / 2;
    const rInner = rOuter - Math.max(3, sw * 1.5);
    const cx = w / 2;
    const cy = h / 2;
    return (
      <svg width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <circle cx={cx} cy={cy} r={rOuter} {...common} />
        <circle cx={cx} cy={cy} r={rInner} fill="none" stroke={stroke} strokeWidth={sw} />
        {shape === 'bpmn_event_timer' ? (
          <g stroke={stroke} strokeWidth={sw} strokeLinecap="round" fill="none">
            <circle cx={cx} cy={cy} r={rInner * 0.62} />
            <line x1={cx} y1={cy} x2={cx} y2={cy - rInner * 0.42} />
            <line x1={cx} y1={cy} x2={cx + rInner * 0.32} y2={cy} />
            {Array.from({ length: 12 }, (_, i) => {
              const angle = (i * Math.PI) / 6;
              const rTick = rInner * 0.62;
              const x1 = cx + Math.sin(angle) * rTick;
              const y1 = cy - Math.cos(angle) * rTick;
              const x2 = cx + Math.sin(angle) * (rTick - 3);
              const y2 = cy - Math.cos(angle) * (rTick - 3);
              return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} />;
            })}
          </g>
        ) : (
          <g stroke={stroke} strokeWidth={sw} strokeLinejoin="round" strokeLinecap="round" fill="none">
            <rect
              x={cx - rInner * 0.55}
              y={cy - rInner * 0.38}
              width={rInner * 1.1}
              height={rInner * 0.76}
            />
            <path
              d={`M ${cx - rInner * 0.55},${cy - rInner * 0.38} L ${cx},${cy + rInner * 0.06} L ${cx + rInner * 0.55},${cy - rInner * 0.38}`}
            />
          </g>
        )}
      </svg>
    );
  }

  if (
    shape === 'bpmn_gateway' ||
    shape === 'bpmn_gateway_exclusive' ||
    shape === 'bpmn_gateway_inclusive'
  ) {
    const cx = w / 2;
    const cy = h / 2;
    const m = Math.min(w, h) * 0.18; // tamanho do marcador interno, escala com o nó
    return (
      <svg width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <polygon points={`${cx},${sw} ${w - sw},${cy} ${cx},${h - sw} ${sw},${cy}`} {...common} />
        <g stroke={stroke} strokeWidth={sw * 1.3} strokeLinecap="round" fill="none">
          {shape === 'bpmn_gateway' && (
            <>
              {/* Paralelo (AND): cruz "+" */}
              <line x1={cx - m} y1={cy} x2={cx + m} y2={cy} />
              <line x1={cx} y1={cy - m} x2={cx} y2={cy + m} />
            </>
          )}
          {shape === 'bpmn_gateway_exclusive' && (
            <>
              {/* Exclusivo (XOR): "X" */}
              <line x1={cx - m} y1={cy - m} x2={cx + m} y2={cy + m} />
              <line x1={cx + m} y1={cy - m} x2={cx - m} y2={cy + m} />
            </>
          )}
          {shape === 'bpmn_gateway_inclusive' && (
            /* Inclusivo (OR): círculo "O" */
            <circle cx={cx} cy={cy} r={m} />
          )}
        </g>
      </svg>
    );
  }

  if (shape === 'annotation') {
    // Post-it com canto superior direito dobrado.
    const fold = Math.max(12, Math.min(28, Math.min(w, h) * 0.18));
    return (
      <svg width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <path
          d={`M ${sw / 2},${sw / 2}
             L ${w - sw / 2 - fold},${sw / 2}
             L ${w - sw / 2},${sw / 2 + fold}
             L ${w - sw / 2},${h - sw / 2}
             L ${sw / 2},${h - sw / 2} Z`}
          fill={bgColor}
          stroke={stroke}
          strokeWidth={sw}
          strokeLinejoin="round"
        />
        {/* Linha do canto dobrado */}
        <path
          d={`M ${w - sw / 2 - fold},${sw / 2}
             L ${w - sw / 2 - fold},${sw / 2 + fold}
             L ${w - sw / 2},${sw / 2 + fold}`}
          fill="none"
          stroke={stroke}
          strokeWidth={sw}
          strokeLinejoin="round"
        />
      </svg>
    );
  }

  if (shape === 'swimlane') {
    const headerW = 36;
    return (
      <svg width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <rect x={sw / 2} y={sw / 2} width={w - sw} height={h - sw} fill={bgColor} stroke={stroke} strokeWidth={sw} />
        <rect x={sw / 2} y={sw / 2} width={headerW} height={h - sw} fill={borderColor} opacity={0.15} stroke={stroke} strokeWidth={sw} />
      </svg>
    );
  }

  if (shape === 'text') {
    // Texto puro: sem caixa, borda ou preenchimento — só o label (renderizado no ShapeNode).
    return null;
  }

  if (shape === 'container') {
    // Frame agrupador: retângulo tracejado translúcido com faixa de cabeçalho.
    const r = 10;
    const headerH = Math.max(22, Math.min(34, h * 0.16));
    return (
      <svg width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <rect
          x={sw / 2}
          y={sw / 2}
          width={w - sw}
          height={h - sw}
          rx={r}
          ry={r}
          fill={bgColor}
          fillOpacity={0.16}
          stroke={stroke}
          strokeWidth={sw}
          strokeDasharray="7 5"
        />
        <rect
          x={sw / 2 + 1}
          y={sw / 2 + 1}
          width={w - sw - 2}
          height={headerH}
          fill={stroke}
          fillOpacity={0.14}
        />
      </svg>
    );
  }

  if (shape === 'off_page_connector') {
    // Pentágono "casinha" apontando para baixo (conexão para outra página/fluxo).
    const notch = h * 0.28;
    return (
      <svg width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <polygon
          points={`${sw / 2},${sw / 2} ${w - sw / 2},${sw / 2} ${w - sw / 2},${h - notch} ${w / 2},${h - sw / 2} ${sw / 2},${h - notch}`}
          {...common}
        />
      </svg>
    );
  }

  if (shape === 'preparation') {
    // Hexágono alongado (setup/inicialização de etapa).
    const m = w * 0.16;
    return (
      <svg width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <polygon
          points={`${m},${sw / 2} ${w - m},${sw / 2} ${w - sw / 2},${h / 2} ${w - m},${h - sw / 2} ${m},${h - sw / 2} ${sw / 2},${h / 2}`}
          {...common}
        />
      </svg>
    );
  }

  if (shape === 'manual_operation') {
    // Trapézio com o topo mais largo (operação feita manualmente).
    const m = w * 0.14;
    return (
      <svg width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <polygon
          points={`${sw / 2},${sw / 2} ${w - sw / 2},${sw / 2} ${w - m},${h - sw / 2} ${m},${h - sw / 2}`}
          {...common}
        />
      </svg>
    );
  }

  if (shape === 'delay') {
    // Forma em "D": lado direito semicircular (fila/espera/pendência).
    const r = (h - sw) / 2;
    return (
      <svg width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
        <path
          d={`M ${sw / 2},${sw / 2} L ${w - r - sw / 2},${sw / 2} A ${r},${r} 0 0 1 ${w - r - sw / 2},${h - sw / 2} L ${sw / 2},${h - sw / 2} Z`}
          {...common}
        />
      </svg>
    );
  }

  // Figura não tem moldura: o ícone/imagem é desenhado pelo ShapeNode.
  if (shape === 'figura') return null;

  // Fallback
  return (
    <svg width="100%" height="100%" viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none">
      <rect x={sw / 2} y={sw / 2} width={w - sw} height={h - sw} {...common} />
    </svg>
  );
}
